import { zodResolver } from '@hookform/resolvers/zod';
import { FormCheckbox } from '@ipfabric/ui-components/FormCheckbox/FormCheckbox';
import { FormCodeEditor } from '@ipfabric/ui-components/FormCodeEditor/FormCodeEditor';
import { FormSegmentedButton } from '@ipfabric/ui-components/FormSegmentedButton/FormSegmentedButton';
import { FormSelect } from '@ipfabric/ui-components/FormSelect/FormSelect';
import { FormSwitch } from '@ipfabric/ui-components/FormSwitch/FormSwitch';
import { FormTextField } from '@ipfabric/ui-components/FormTextField/FormTextField';
import { SegmentButton } from '@ipfabric/ui-components/SegmentedButton/SegmentButton/SegmentButton';
import { TreePicker } from '@ipfabric/ui-components/TreePicker/TreePicker';
import { useCustomForm } from '@ipfabric/ui-components/utils/hookForm/useCustomForm';
import type { HeaderTemplateProblem } from '@ipfabric/utils/webhooks/headerTemplate';
import { validateHeaderTemplateEntries } from '@ipfabric/utils/webhooks/headerTemplate';
import { Button } from 'components/Button/Button';
import FormErrorMessage from 'components/FormErrorMessage';
import { useLocale } from 'containers/App/LocaleContext';
import {
  type MouseEvent,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useController, useFieldArray, useWatch } from 'react-hook-form';
import type { AppTranslations } from 'translations/translations';
import { z } from 'zod';
import { CaUploadModal } from './CaUploadModal';
import type { RightPanelContentType } from './WebHookDetail';
import {
  type EventType,
  type WebHookType,
  type WebhookPayloadTemplate,
  webHookTypes,
} from './WebHooksState';
import type { SaveWebHookCommand } from './commands/saveWebHook';
import {
  PAYLOAD_FORMATS,
  type PayloadFormat,
  TEMPLATE_VARIABLE_GROUPS,
} from './constants';
import { TRIGGER_TREE_ITEMS } from './helpers/triggerTreeItems';
import { useTypeTranslator } from './helpers/useTypeTranslator';

type ValidationMessages =
  AppTranslations['settings']['webHooks']['form']['validationMessages'];

// The API keeps its terse strings (see formatHeaderTemplateProblem in
// @ipfabric/utils/webhooks/headerTemplate); the UI gets localized ones, both
// driven off the same `problem.code`.
const headerProblemMessage = (
  problem: HeaderTemplateProblem,
  messages: ValidationMessages,
): string => {
  switch (problem.code) {
    case 'invalidName':
      return messages.headerInvalidName(problem.name);
    case 'duplicateName':
      return messages.headerDuplicateName(problem.name);
    case 'reservedName':
      return messages.headerReservedName(problem.name);
    case 'newline':
      return messages.headerValueNewline;
    case 'invalidTemplate':
      return messages.headerInvalidTemplate;
    case 'unsupportedVariable':
      return messages.headerUnsupportedVariable(problem.variable);
  }
};

type HeaderRow = { key: string; value: string };
type HeaderIssue = { path: [string, number, string]; message: string };

// Custom headers only apply to fine-grained (trigger) webhooks; a legacy
// webhook's rows are dropped on save (see `serializeHeaders` in
// commands/saveWebHook.tsx) and so are not validated here — callers must gate
// this on `useTriggers`.
const getHeaderIssues = (
  headers: HeaderRow[],
  messages: ValidationMessages,
): HeaderIssue[] => {
  // Mirror `serializeHeaders`: names are trimmed and blank-named rows dropped
  // before the map is sent, so validate exactly what the API will see.
  const rows = headers
    .map(({ key, value }, index) => ({
      index,
      entry: [key.trim(), value] as const,
    }))
    .filter(({ entry }) => entry[0] !== '');

  const templateIssues = validateHeaderTemplateEntries(
    rows.map((row) => row.entry),
  ).flatMap((problem): HeaderIssue[] => {
    const row = rows[problem.index];
    if (!row) {
      return []; // noUncheckedIndexedAccess
    }
    return [
      {
        path: [
          'headers',
          row.index,
          problem.field === 'name' ? 'key' : 'value',
        ],
        message: headerProblemMessage(problem, messages),
      },
    ];
  });

  // A row with a value but no name is dropped by `serializeHeaders`, so the
  // value would silently vanish on save; the API tolerates it, the UI
  // doesn't. A fully blank row (fresh "Add header") stays valid.
  const missingNameIssues = headers.flatMap(
    ({ key, value }, index): HeaderIssue[] =>
      key.trim() === '' && value.trim() !== ''
        ? [{ path: ['headers', index, 'key'], message: messages.required }]
        : [],
  );

  return [...templateIssues, ...missingNameIssues];
};

const addHeaderIssues = (
  ctx: z.RefinementCtx,
  headers: HeaderRow[],
  messages: ValidationMessages,
): void => {
  for (const issue of getHeaderIssues(headers, messages)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, ...issue });
  }
};

export interface WebHookValues {
  active: boolean;
  id?: string;
  secret?: string;
  name: string;
  sendAll: boolean;
  useTriggers: boolean;
  trigger: Set<string>;
  types: Record<WebHookType, boolean>;
  payloadTemplate: WebhookPayloadTemplate;
  // Ordered, editable rows; serialized to a `{ name: value }` map on save.
  headers: { key: string; value: string }[];
  url: string;
  // Outbound auth token (write-only). `token` is the new value to save;
  // `hasToken` reflects whether one is already stored; `clearToken` removes it.
  token: string;
  clearToken: boolean;
  hasToken?: boolean;
  caCertId: string | null;
}

export interface WebHookFormHandle {
  submitForm: () => void;
}

interface Props {
  caCertOptions: { label: string; value: string }[];
  eventTypes: EventType[];
  handleResetRightPanelContent: (e: MouseEvent) => void;
  initialValues: WebHookValues;
  makeSetRightPanelContent: (
    content: RightPanelContentType,
  ) => (e: MouseEvent) => void;
  reloadCACerts: () => void | Promise<void>;
  saveWebHook: (command: SaveWebHookCommand) => void;
  selectedHelp?: WebHookType;
}

export const WebHookForm = forwardRef<WebHookFormHandle, Props>(
  (
    {
      caCertOptions,
      eventTypes,
      handleResetRightPanelContent,
      initialValues,
      makeSetRightPanelContent,
      reloadCACerts,
      saveWebHook,
      selectedHelp,
    },
    ref,
  ) => {
    const msg = useLocale();
    const getTypeTranslation = useTypeTranslator();
    const { validationMessages } = msg.settings.webHooks.form;

    const schema = useMemo(() => {
      const {
        required,
        invalidUrl,
        lengthMax64,
        atLeastOneEventType,
        atLeastOneTrigger,
      } = validationMessages;

      const typesShape = webHookTypes.reduce(
        (acc, type) => {
          acc[type] = z.boolean();
          return acc;
        },
        {} as Record<WebHookType, z.ZodBoolean>,
      );

      return z
        .object({
          active: z.boolean(),
          id: z.string().optional(),
          secret: z.string().optional(),
          name: z.string().min(1, required).max(64, lengthMax64),
          url: z
            .string()
            .min(1, required)
            .regex(/https?/i, invalidUrl),
          sendAll: z.boolean(),
          useTriggers: z.boolean().default(false),
          trigger: z.set(z.string()),
          types: z.object(typesShape),
          payloadTemplate: z.object({
            format: z.enum(PAYLOAD_FORMATS),
            template: z.string(),
          }),
          headers: z.array(z.object({ key: z.string(), value: z.string() })),
          token: z.string(),
          clearToken: z.boolean(),
          hasToken: z.boolean().optional(),
          caCertId: z.string().nullable(),
        })
        .superRefine((values, ctx) => {
          // Trigger mode and legacy type mode are mutually exclusive; validate
          // whichever one the `useTriggers` toggle has active.
          if (values.useTriggers) {
            if (values.trigger.size === 0) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: atLeastOneTrigger,
                path: ['trigger'],
              });
            }
          } else if (
            !values.sendAll &&
            !webHookTypes.some((key) => values.types[key] === true)
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: atLeastOneEventType,
              path: ['types'],
            });
          }

          // Custom headers only apply to fine-grained (trigger) webhooks; a
          // legacy webhook's rows are dropped on save and so are not
          // validated here (see `getHeaderIssues`).
          if (values.useTriggers) {
            addHeaderIssues(ctx, values.headers, validationMessages);
          }
        });
    }, [validationMessages]);

    const { control, handleSubmit, setValue, formState } =
      useCustomForm<WebHookValues>({
        defaultValues: initialValues,
        resolver: zodResolver(schema),
      });

    const onSubmit = (values: WebHookValues) => {
      saveWebHook({ values });
    };

    useImperativeHandle(ref, () => ({
      submitForm: handleSubmit(onSubmit),
    }));

    const [showCaUpload, setShowCaUpload] = useState(false);

    const { field: triggerField } = useController({ control, name: 'trigger' });
    const {
      fields: headerFields,
      append: appendHeader,
      remove: removeHeader,
    } = useFieldArray({ control, name: 'headers' });

    const useTriggers = useWatch({ control, name: 'useTriggers' });
    const sendAll = useWatch({ control, name: 'sendAll' });
    const payloadFormat = useWatch({ control, name: 'payloadTemplate.format' });
    const hasToken = useWatch({ control, name: 'hasToken' });
    const clearToken = useWatch({ control, name: 'clearToken' });

    // Selecting "send all" implies every event type is selected, mirroring the
    // legacy toggle behavior (it only sets, never clears, on enable).
    useEffect(() => {
      if (sendAll) {
        for (const type of webHookTypes) {
          setValue(`types.${type}`, true);
        }
      }
    }, [sendAll, setValue]);

    // Trigger mode and legacy mode are mutually exclusive, so flipping the
    // toggle must reset the mode being left to a valid default. Guard against
    // the initial mount so loaded values aren't clobbered while editing.
    const prevUseTriggers = useRef(useTriggers);
    useEffect(() => {
      if (prevUseTriggers.current === useTriggers) {
        return;
      }
      prevUseTriggers.current = useTriggers;

      if (useTriggers) {
        // Entering trigger mode: clear the legacy selection.
        setValue('sendAll', false);
        for (const type of webHookTypes) {
          setValue(`types.${type}`, false);
        }
      } else {
        // Leaving trigger mode: drop the trigger and restore the legacy default.
        setValue('trigger', new Set());
        setValue('sendAll', true);
      }
    }, [useTriggers, setValue]);

    const typesError = formState.errors.types?.message?.toString();
    const triggerError = formState.errors.trigger?.message?.toString();

    return (
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormSwitch
          control={control}
          name='active'
          label={msg.settings.webHooks.form.active}
        />
        <div className='mt-4'>
          <FormTextField
            control={control}
            name='name'
            type='text'
            label={msg.settings.webHooks.form.name}
          />
        </div>
        <div className='mt-4'>
          <FormTextField
            control={control}
            name='url'
            type='text'
            label={msg.settings.webHooks.form.url}
          />
        </div>
        {/* Outbound auth token and the custom CA are fine-grained (trigger)
            features only; legacy webhooks don't render or send them and use the
            appliance default trust store. */}
        {useTriggers && (
          <div className='mt-4'>
            <h4>{msg.settings.webHooks.form.authenticationLabel}</h4>
            <div className='mt-2'>
              <FormTextField
                control={control}
                name='token'
                type='password'
                label={msg.settings.webHooks.form.token}
                disabled={clearToken}
              />
              <p className='mt-1 text-gray-500'>
                {hasToken
                  ? msg.settings.webHooks.form.tokenHintConfigured
                  : msg.settings.webHooks.form.tokenHintNew}
              </p>
              {hasToken && (
                <div className='mt-2'>
                  <FormCheckbox
                    control={control}
                    name='clearToken'
                    label={msg.settings.webHooks.form.clearToken}
                  />
                </div>
              )}
            </div>
            {/* Custom CA for outbound TLS: pick an existing CA or upload a new
                one inline (like the LDAP form). Empty → appliance default trust. */}
            <div className='mt-4 flex items-end gap-2'>
              <div className='flex-1'>
                <FormSelect.Optional
                  control={control}
                  name='caCertId'
                  label={msg.settings.webHooks.form.caCert}
                  options={caCertOptions}
                  placeholder={msg.settings.webHooks.form.caCertPlaceholder}
                  clearable
                />
              </div>
              <Button
                variant='secondary'
                type='button'
                icon='fa-upload'
                onClick={() => setShowCaUpload(true)}
              >
                {msg.settings.webHooks.form.caCertUpload}
              </Button>
            </div>
          </div>
        )}
        <hr className='border-gray-600' />
        <div className='max-h-[60vh] overflow-y-auto'>
          <div className='mb-4'>
            <FormSwitch
              control={control}
              name='useTriggers'
              label={msg.settings.webHooks.form.useFineGrainedTriggers}
            />
          </div>

          {useTriggers ? (
            <div>
              <h4>{msg.settings.webHooks.form.triggersLabel}</h4>
              <div className='mt-4 overflow-y-auto'>
                <TreePicker
                  items={TRIGGER_TREE_ITEMS}
                  value={triggerField.value}
                  onChange={triggerField.onChange}
                  showSelected={false}
                  noScroll={true}
                />
              </div>
              {triggerError && (
                <FormErrorMessage>{triggerError}</FormErrorMessage>
              )}
            </div>
          ) : (
            <div>
              <h4>{msg.settings.webHooks.form.eventsLabel}</h4>

              <div className='mt-6'>
                <div className='mb'>
                  <FormSwitch
                    control={control}
                    name='sendAll'
                    label={
                      sendAll
                        ? msg.settings.webHooks.form.sendAll
                        : msg.settings.webHooks.form.selectIndividual
                    }
                  />
                </div>

                {eventTypes.map(
                  ({ type, description: { general, payload } }) => (
                    <div
                      key={type}
                      className='WebHook__type-checkbox flex items-center gap-2'
                    >
                      <FormCheckbox
                        control={control}
                        name={`types.${type}`}
                        disabled={sendAll}
                        description={getTypeTranslation(type)}
                      />

                      {type === selectedHelp ? (
                        <Button
                          variant='tertiary'
                          onClick={handleResetRightPanelContent}
                          type='button'
                        >
                          {msg.settings.webHooks.form.closeHelp}
                        </Button>
                      ) : (
                        <Button
                          variant='tertiary'
                          onClick={makeSetRightPanelContent({
                            general,
                            payload,
                            type,
                          })}
                          type='button'
                        >
                          {msg.settings.webHooks.form.showHelp}
                        </Button>
                      )}
                    </div>
                  ),
                )}

                {typesError && (
                  <FormErrorMessage>{typesError}</FormErrorMessage>
                )}
              </div>
            </div>
          )}
        </div>
        {/* Payload templates only apply to fine-grained (trigger) webhooks. */}
        {useTriggers && (
          <>
            <hr className='border-gray-600' />
            <div className='mt-4'>
              <FormSegmentedButton.Single<WebHookValues, PayloadFormat>
                control={control}
                name='payloadTemplate.format'
                label={msg.settings.webHooks.form.payloadFormat}
              >
                {PAYLOAD_FORMATS.map((format) => (
                  <SegmentButton key={format} value={format} className='flex-1'>
                    {format.toUpperCase()}
                  </SegmentButton>
                ))}
              </FormSegmentedButton.Single>
            </div>
            <div className='mt-4'>
              <h4>{msg.settings.webHooks.form.availableVariables}</h4>
              <p className='mt-1 text-gray-500'>
                {msg.settings.webHooks.form.availableVariablesHint}
              </p>
              {[
                {
                  label: msg.settings.webHooks.form.variableGroupCommon,
                  vars: TEMPLATE_VARIABLE_GROUPS.common,
                },
                {
                  label: msg.settings.webHooks.form.variableGroupSnapshot,
                  vars: TEMPLATE_VARIABLE_GROUPS.snapshot,
                },
                {
                  label: msg.settings.webHooks.form.variableGroupIntentCheck,
                  vars: TEMPLATE_VARIABLE_GROUPS.intentCheck,
                },
              ].map(({ label, vars }) => (
                <div
                  key={label}
                  className='mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-1'
                >
                  <span className='font-medium'>{label}:</span>
                  {vars.map((variable) => (
                    <code key={variable} className='text-sm'>
                      {variable}
                    </code>
                  ))}
                </div>
              ))}
            </div>
            <div className='mt-4'>
              <FormCodeEditor
                control={control}
                name='payloadTemplate.template'
                language={payloadFormat === 'xml' ? 'mustache' : 'json'}
                label={msg.settings.webHooks.form.payloadTemplate}
                minHeight='24rem'
                showLineNumbers
              />
            </div>
            <hr className='border-gray-600' />
            <div className='mt-4'>
              <h4>{msg.settings.webHooks.form.headersLabel}</h4>
              <p className='mt-1 text-gray-500'>
                {msg.settings.webHooks.form.headersHint}
              </p>
              {headerFields.map((field, index) => (
                <div key={field.id} className='mt-2 flex items-start gap-2'>
                  <div className='flex-1'>
                    <FormTextField
                      control={control}
                      name={`headers.${index}.key`}
                      type='text'
                      placeholder={msg.settings.webHooks.form.headerKey}
                    />
                  </div>
                  <div className='flex-1'>
                    <FormTextField
                      control={control}
                      name={`headers.${index}.value`}
                      type='text'
                      placeholder={msg.settings.webHooks.form.headerValue}
                    />
                  </div>
                  <Button
                    variant='tertiary'
                    type='button'
                    icon='fa-trash-can'
                    onClick={() => removeHeader(index)}
                  />
                </div>
              ))}
              <div className='mt-2'>
                <Button
                  variant='secondary'
                  accent='positive'
                  size='xs'
                  type='button'
                  icon='fa-plus'
                  onClick={() => appendHeader({ key: '', value: '' })}
                >
                  {msg.settings.webHooks.form.addHeader}
                </Button>
              </div>
            </div>
          </>
        )}
        {showCaUpload && (
          <CaUploadModal
            onClose={() => setShowCaUpload(false)}
            onUploaded={async (ca) => {
              await reloadCACerts();
              setValue('caCertId', ca.id);
            }}
          />
        )}
      </form>
    );
  },
);
